import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

function safeNum(value) {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

function hasNumber(value) {
  if (value === null || value === undefined || value === "") {
    return false;
  }

  return Number.isFinite(Number(value));
}

function money(value) {
  return new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: "GBP",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(safeNum(value));
}

function percent(value) {
  if (value === null || value === undefined) {
    return "—";
  }

  return `${safeNum(value).toFixed(2)}%`;
}

function dateFromAny(value) {
  if (!value) {
    return null;
  }

  if (typeof value?.toDate === "function") {
    return value.toDate();
  }

  if (value instanceof Date) {
    return value;
  }

  if (
    typeof value === "object" &&
    Number.isFinite(Number(value.seconds))
  ) {
    return new Date(Number(value.seconds) * 1000);
  }

  const parsed = new Date(value);

  if (Number.isNaN(parsed.getTime())) {
    return null;
  }

  return parsed;
}

export function stockReportDate(value) {
  const date = dateFromAny(value);

  if (!date) {
    return "—";
  }

  return new Intl.DateTimeFormat("en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  }).format(date);
}

function stockReportISODate(value) {
  const date = dateFromAny(value);

  if (!date) {
    return "undated";
  }

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
}

export async function buildStockCountSheetPdf(
  {
    rows = [],
    venueId = "",
    venueName = "",
    generatedAt = new Date(),
  } = {},
  onProgress
) {
  const progress = (percentage, message) => {
    if (typeof onProgress === "function") {
      onProgress(percentage, message);
    }
  };

  progress(5, "Preparing stock count sheet...");

  const items = (Array.isArray(rows) ? rows : [])
    .map((row) => ({
      product_name: String(row?.product_name || "—").trim(),
      product_group: String(row?.product_group || "Other").trim(),
      supplier: String(row?.supplier || "—").trim(),
      unit_size: String(row?.unit_size || "—").trim(),
      opening_count: safeNum(row?.opening_count),
      stock_in: safeNum(row?.stock_in),
      stock_out: safeNum(row?.stock_out),
    }))
    .sort(
      (a, b) =>
        a.product_group.localeCompare(b.product_group, "en-GB", {
          sensitivity: "base",
        }) ||
        a.product_name.localeCompare(b.product_name, "en-GB", {
          sensitivity: "base",
        })
    );

  if (!items.length) {
    throw new Error(
      "There are no stock products to include in the count sheet."
    );
  }

  const doc = new jsPDF({
    orientation: "landscape",
    unit: "pt",
    format: "a4",
  });

  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const venueLabel = String(venueName || venueId || "—").trim();
  const generatedDate = dateFromAny(generatedAt) || new Date();

  const generatedLabel = new Intl.DateTimeFormat("en-GB", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(generatedDate);

  doc.setFont("helvetica", "bold");
  doc.setFontSize(20);
  doc.text("Stock Count Sheet", 30, 36);

  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.text(`Venue: ${venueLabel}`, 30, 55);
  doc.text(`Generated: ${generatedLabel}`, pageWidth - 30, 55, {
    align: "right",
  });

  doc.setFontSize(9);
  doc.text("Counted by: ______________________________", 30, 78);
  doc.text("Checked by: ______________________________", 290, 78);
  doc.text("Count date: ______________________________", 550, 78);

  doc.setTextColor(80);
  doc.text(
    "Record the physical quantity in Closing Count, then enter it into CoreDeck.",
    30,
    98
  );
  doc.setTextColor(0);

  progress(35, "Rendering product lines...");

  autoTable(doc, {
    startY: 112,
    head: [
      [
        "Product Group",
        "Product",
        "Supplier",
        "Unit Size",
        "Opening",
        "Stock In",
        "Stock Out",
        "Closing Count",
      ],
    ],
    body: items.map((item) => [
      item.product_group,
      item.product_name,
      item.supplier,
      item.unit_size,
      item.opening_count.toFixed(2),
      item.stock_in.toFixed(2),
      item.stock_out.toFixed(2),
      "",
    ]),
    styles: {
      fontSize: 8,
      cellPadding: 4,
      lineColor: [170, 170, 170],
      lineWidth: 0.35,
      overflow: "linebreak",
      valign: "middle",
    },
    headStyles: {
      fillColor: [55, 71, 79],
      textColor: 255,
      fontStyle: "bold",
    },
    alternateRowStyles: {
      fillColor: [245, 247, 248],
    },
    columnStyles: {
      0: { cellWidth: 105 },
      1: { cellWidth: 170 },
      2: { cellWidth: 120 },
      3: { cellWidth: 75 },
      4: { cellWidth: 65, halign: "right" },
      5: { cellWidth: 65, halign: "right" },
      6: { cellWidth: 65, halign: "right" },
      7: { cellWidth: 95, minCellHeight: 24 },
    },
    margin: {
      top: 30,
      right: 30,
      bottom: 30,
      left: 30,
    },
    didDrawPage: () => {
      const page = doc.internal.getNumberOfPages();

      doc.setFont("helvetica", "normal");
      doc.setFontSize(8);
      doc.setTextColor(110);
      doc.text(
        `Page ${page} · ${items.length} products`,
        pageWidth - 30,
        pageHeight - 14,
        { align: "right" }
      );
      doc.setTextColor(0);
    },
  });

  const safeVenue = String(venueName || venueId || "venue")
    .trim()
    .replace(/[^a-z0-9_-]+/gi, "-")
    .replace(/^-+|-+$/g, "");

  const filename =
    `stock-count-sheet-${safeVenue || "venue"}-` +
    `${stockReportISODate(generatedDate)}.pdf`;

  progress(100, "Done");

  return {
    doc,
    filename,
  };
}

function salesInStockUnits(row) {
  if (hasNumber(row?.sales_stock_units)) {
    return safeNum(row.sales_stock_units);
  }

  const rawSales = safeNum(row?.sales);
  const conversion = safeNum(row?.stock_units_per_unit);

  if (conversion <= 0) {
    return 0;
  }

  return rawSales / conversion;
}

export function buildStockReportData({
  rows = [],
  turnover = null,
} = {}) {
  const items = (Array.isArray(rows) ? rows : []).map((row) => {
    const unitCost = safeNum(row?.unit_cost);
    const opening = safeNum(row?.opening_count);
    const stockIn = safeNum(row?.stock_in);
    const stockOut = safeNum(row?.stock_out);
    const closing = safeNum(row?.closing_count);
    const rawSales = safeNum(row?.sales);
    const convertedSales = salesInStockUnits(row);
    const availableStock = opening + stockIn - stockOut;
    const consumption = availableStock - closing;
    const calculatedVarianceUnits = convertedSales - consumption;

    const varianceUnits = hasNumber(row?.variance_units)
      ? safeNum(row.variance_units)
      : calculatedVarianceUnits;

    const calculatedVarianceValue = varianceUnits * unitCost;

    const varianceValue = hasNumber(row?.variance_value)
      ? safeNum(row.variance_value)
      : calculatedVarianceValue;

    const openingValue = opening * unitCost;
    const stockInValue = stockIn * unitCost;
    const stockOutValue = stockOut * unitCost;
    const availableValue = availableStock * unitCost;
    const closingValue = closing * unitCost;
    const theoreticalCost = convertedSales * unitCost;
    const actualCost = consumption * unitCost;

    return {
      product_id: String(row?.product_id ?? ""),
      product_key: String(row?.product_key ?? ""),
      product_name: row?.product_name || "—",
      product_group: row?.product_group || "—",
      supplier: row?.supplier || "—",
      unit_size: row?.unit_size || "—",
      unit_cost: unitCost,
      stock_units_per_unit: safeNum(row?.stock_units_per_unit),
      opening_count: opening,
      stock_in: stockIn,
      stock_out: stockOut,
      closing_count: closing,
      available_stock: availableStock,
      consumption,
      stock_in_from_deliveries: safeNum(
        row?.stock_in_from_deliveries
      ),
      stock_in_from_transfers: safeNum(
        row?.stock_in_from_transfers
      ),
      stock_out_from_transfers: safeNum(
        row?.stock_out_from_transfers
      ),
      stock_in_overridden: row?.stock_in_overridden === true,
      stock_out_overridden: row?.stock_out_overridden === true,
      sales: rawSales,
      sales_stock_units: convertedSales,
      variance_units: varianceUnits,
      variance_value: varianceValue,
      opening_value: openingValue,
      stock_in_value: stockInValue,
      stock_out_value: stockOutValue,
      available_value: availableValue,
      closing_value: closingValue,
      theoretical_cost: theoreticalCost,
      actual_cost: actualCost,
    };
  });

  const totals = items.reduce(
    (accumulator, item) => {
      accumulator.opening_value += item.opening_value;
      accumulator.stock_in_value += item.stock_in_value;
      accumulator.stock_out_value += item.stock_out_value;
      accumulator.available_value += item.available_value;
      accumulator.closing_value += item.closing_value;
      accumulator.theoretical_cost += item.theoretical_cost;
      accumulator.actual_cost += item.actual_cost;
      accumulator.variance_value += item.variance_value;
      accumulator.opening_units += item.opening_count;
      accumulator.stock_in_units += item.stock_in;
      accumulator.stock_out_units += item.stock_out;
      accumulator.closing_units += item.closing_count;
      accumulator.consumption_units += item.consumption;
      accumulator.sales_stock_units += item.sales_stock_units;
      accumulator.variance_units += item.variance_units;
      accumulator.delivery_units += item.stock_in_from_deliveries;
      accumulator.transfer_in_units += item.stock_in_from_transfers;
      accumulator.transfer_out_units += item.stock_out_from_transfers;

      if (item.stock_in_overridden) {
        accumulator.stock_in_override_count += 1;
      }

      if (item.stock_out_overridden) {
        accumulator.stock_out_override_count += 1;
      }

      return accumulator;
    },
    {
      opening_value: 0,
      stock_in_value: 0,
      stock_out_value: 0,
      available_value: 0,
      closing_value: 0,
      theoretical_cost: 0,
      actual_cost: 0,
      variance_value: 0,
      opening_units: 0,
      stock_in_units: 0,
      stock_out_units: 0,
      closing_units: 0,
      consumption_units: 0,
      sales_stock_units: 0,
      variance_units: 0,
      delivery_units: 0,
      transfer_in_units: 0,
      transfer_out_units: 0,
      stock_in_override_count: 0,
      stock_out_override_count: 0,
    }
  );

  const hasTurnover =
    !!turnover &&
    (hasNumber(turnover?.net) || hasNumber(turnover?.gross));

  const gross = hasTurnover ? safeNum(turnover?.gross) : null;
  const vat = hasTurnover ? safeNum(turnover?.vat) : null;
  const net = hasTurnover ? safeNum(turnover?.net) : null;

  let actualGpPounds = null;
  let theoreticalGpPounds = null;
  let varianceGpPounds = null;
  let actualGpPercent = null;
  let theoreticalGpPercent = null;
  let varianceGpPercent = null;

  if (hasTurnover && net !== null) {
    actualGpPounds = net - totals.actual_cost;
    theoreticalGpPounds = net - totals.theoretical_cost;
    varianceGpPounds = actualGpPounds - theoreticalGpPounds;

    if (net !== 0) {
      actualGpPercent = (actualGpPounds / net) * 100;
      theoreticalGpPercent = (theoreticalGpPounds / net) * 100;
      varianceGpPercent = actualGpPercent - theoreticalGpPercent;
    }
  }

  const stockVariancePercent =
    totals.theoretical_cost !== 0
      ? (totals.variance_value / totals.theoretical_cost) * 100
      : 0;

  return {
    items,
    totals,
    turnover: {
      available: hasTurnover,
      gross,
      vat,
      net,
    },
    gp: {
      actual_pounds: actualGpPounds,
      theoretical_pounds: theoreticalGpPounds,
      variance_pounds: varianceGpPounds,
      actual_percent: actualGpPercent,
      theoretical_percent: theoreticalGpPercent,
      variance_percent: varianceGpPercent,
    },
    stock_variance_percent: stockVariancePercent,
    product_count: items.length,
  };
}

export async function buildStockPdfReport(
  {
    rows = [],
    venueId = "",
    venueName = "",
    completedAt = null,
    countId = "",
    turnover = null,
  } = {},
  onProgress
) {
  const progress = (percentage, message) => {
    if (typeof onProgress === "function") {
      onProgress(percentage, message);
    }
  };

  progress(5, "Preparing stock report...");

  const report = buildStockReportData({
    rows,
    turnover,
  });

  const { items, totals, gp } = report;

  progress(25, "Building summary...");

  const doc = new jsPDF({
    orientation: "landscape",
    unit: "pt",
    format: "a4",
  });

  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();

  doc.setFont("helvetica", "bold");
  doc.setFontSize(16);
  doc.text("Stock Count Report", 40, 38);

  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);

  const displayVenue = venueName || venueId || "—";

  doc.text(`Venue: ${displayVenue}`, 40, 56);
  doc.text(`Completed: ${stockReportDate(completedAt)}`, 40, 70);

  if (countId) {
    doc.text(`Count ID: ${countId}`, 40, 84);
  }

  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);

  doc.text("Opening Stock", 40, 112);
  doc.text(money(totals.opening_value), 140, 112);

  doc.text("Stock In", 40, 130);
  doc.text(money(totals.stock_in_value), 140, 130);

  doc.text("Stock Out", 40, 148);
  doc.text(money(totals.stock_out_value), 140, 148);

  doc.text("Closing Stock", 40, 166);
  doc.text(money(totals.closing_value), 140, 166);

  doc.text("Theoretical Cost", 260, 112);
  doc.text(money(totals.theoretical_cost), 375, 112);

  doc.text("Actual Cost", 260, 130);
  doc.text(money(totals.actual_cost), 375, 130);

  doc.text("Variance", 260, 148);
  doc.text(money(totals.variance_value), 375, 148);

  doc.text("Variance %", 260, 166);
  doc.text(percent(report.stock_variance_percent), 375, 166);

  doc.text("Deliveries", 495, 112);
  doc.text(totals.delivery_units.toFixed(2), 585, 112);

  doc.text("Transfers In", 495, 130);
  doc.text(totals.transfer_in_units.toFixed(2), 585, 130);

  doc.text("Transfers Out", 495, 148);
  doc.text(totals.transfer_out_units.toFixed(2), 585, 148);

  doc.text("Products", 495, 166);
  doc.text(String(report.product_count), 585, 166);

  if (report.turnover.available) {
    doc.text("Net Turnover", 675, 112);
    doc.text(money(report.turnover.net), 765, 112);

    doc.text("Actual GP", 675, 130);
    doc.text(percent(gp.actual_percent), 765, 130);

    doc.text("Theor. GP", 675, 148);
    doc.text(percent(gp.theoretical_percent), 765, 148);

    doc.text("GP Var", 675, 166);
    doc.text(percent(gp.variance_percent), 765, 166);
  }

  progress(50, "Rendering stock lines...");

  autoTable(doc, {
    startY: 195,
    head: [
      [
        "Product",
        "Group",
        "Supplier",
        "Unit",
        "Cost",
        "Opening",
        "In",
        "Out",
        "Closing",
        "Usage",
        "POS Sales",
        "Sales Stock",
        "Var Units",
        "Var £",
      ],
    ],
    body: items.map((item) => [
      item.product_name,
      item.product_group,
      item.supplier,
      item.unit_size,
      money(item.unit_cost),
      item.opening_count.toFixed(2),
      item.stock_in.toFixed(2),
      item.stock_out.toFixed(2),
      item.closing_count.toFixed(2),
      item.consumption.toFixed(2),
      item.sales.toFixed(2),
      item.sales_stock_units.toFixed(2),
      item.variance_units.toFixed(2),
      money(item.variance_value),
    ]),
    styles: {
      fontSize: 7,
      cellPadding: 2.5,
      overflow: "linebreak",
    },
    headStyles: {
      fontStyle: "bold",
    },
    margin: {
      left: 30,
      right: 30,
    },
    didDrawPage: () => {
      const page = doc.internal.getNumberOfPages();

      doc.setFontSize(8);
      doc.setTextColor(120);
      doc.text(`Page ${page}`, pageWidth - 70, pageHeight - 18);
      doc.setTextColor(0);
    },
  });

  progress(90, "Finalising PDF...");

  const safeVenue = String(venueName || venueId || "venue")
    .trim()
    .replace(/[^a-z0-9_-]+/gi, "-")
    .replace(/^-+|-+$/g, "");

  const filename =
    `stock-count-${safeVenue || "venue"}-` +
    `${stockReportISODate(completedAt)}.pdf`;

  progress(100, "Done");

  return {
    doc,
    filename,
    report,
  };
}